import React, { useEffect, useState } from "react";
import {
  useForm,
  Controller,
  UseFormWatch,
  UseFormReturn
} from "react-hook-form";
import { Auth } from "aws-amplify";
import { EMAIL_REGEX, PASSWORD_REGEX } from "constant";
import { LogoHeader } from "./LogoHeader";
import { SIGN_UP } from "queries";
import { SSOIcons } from "../Login/SSOIcons";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "providers/Notification";
import BackButton from "components/BackButton/BackButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import DoneImage from "assets/images/done_all.png";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import PasswordField from "components/PasswordField";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import TermsAndCondition from "components/TermsAndCondition/TermsAndCondition";

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { formatPhoneNumber } from "utils";
import moment from "moment";

interface SignUpFormValues {
  firstName: string;
  lastName: string;
  birthday: string;
  email: string;
  password: string;
  confirmPassword: string;
  zipCode: string;
  phoneNumber: string;
  terms: boolean;

}

export default function FanSignUpForm() {
  const notify = useNotifications();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [signUp] = useMutation(SIGN_UP);


  const form = useForm<SignUpFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      birthday: "",
      email: "",
      password: "",
      confirmPassword: "",
      zipCode: "",
      phoneNumber: "",
      terms: false,
    },
    mode: "all",
  });

  const onSubmit = async ({
    firstName,
    lastName,
    birthday,
    email,
    password,
    zipCode,
    phoneNumber,
  }: SignUpFormValues) => {
    // NOTE: The role and birthday are placeholder with fake data
    // We need these fields in the mutation to be accepted by the backend
    // TODO: delete placeholder role and birthday after the backend is updated.
    const role = "temp";
    // const birthday = "1980-01-01";

    try {
      const response = await signUp({
        variables: {
          data: { firstName, lastName, email, password, birthday, role, phoneNumber, zipCode },
        },
      });
      if (response) {
        setStep(3);
      }
    } catch (error) {
      notify.error(error);
    }
  };

  const handleClose = () => {
    navigate("/login");
  };

  const props = {
    form,
    setStep,
    handleClose,
  };

  if (step === 3) {
    return (
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={10} md={4} lg={3} marginTop={8}>
          <Step3 watch={form.watch} />
        </Grid>
      </Grid>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      {step === 1 && (
        <Grid container spacing={2} justifyContent="center">
          <LogoHeader />
          <Grid item xs={10} md={4} lg={3} marginTop={8}>
            <Step1 {...props} />
          </Grid>
        </Grid>
      )}
      {step === 2 && (
        <Grid container spacing={2} justifyContent="center">
          <LogoHeader />
          <Grid item xs={10} md={4} lg={3} marginTop={8}>
            <BackButton onClick={() => setStep(1)} />
            <Step2 {...props} />
          </Grid>
        </Grid>
      )}
    </form>
  );
}

interface FormStepProps {
  form: UseFormReturn<SignUpFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  handleClose: () => void;
}

const Step1 = ({ form, setStep, handleClose }: FormStepProps) => {
  const { isDirty, errors, isSubmitting, touchedFields } = form.formState;
  const passwordValue = form.watch("password");
  const confirmPasswordValue = form.watch("confirmPassword");
  const checkedTermsAndConditions = form.watch("terms");

  useEffect(() => {
    // NOTE - trigger validation when any of the password fields change
    if (passwordValue && confirmPasswordValue) {
      form.trigger(["password", "confirmPassword"]);
    }
  }, [passwordValue, confirmPasswordValue]);

  const isFirstStepValid = () => {
    const noErrors = () => {
      const { email, password, confirmPassword, terms } = errors;
      return !email && !password && !confirmPassword && !terms;
    };

    const touchedAllFields = () => {
      const { email, password, confirmPassword } = touchedFields;
      return email && password && confirmPassword;
    };

    return Boolean(
      noErrors() && touchedAllFields() && checkedTermsAndConditions
    );
  };




  return (
    <>
      <Stack direction="column" alignItems="flex-start" spacing={1}>
        <Typography marginBottom={2} fontWeight="light">
          Enter your info below to get started!
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          inputProps={{ "data-testid": "email" }}
          label="Email"
          placeholder="Enter email"
          InputLabelProps={{
            shrink: true,
          }}
          type="text"
          fullWidth
          error={Boolean(isDirty && errors.email)}
          disabled={isSubmitting}
          helperText={(isDirty && errors.email?.message) || " "}
          {...form.register("email", {
            required: "Email is required",
            pattern: {
              value: EMAIL_REGEX,
              message: "Invalid email address",
            },
          })}

          onChange={() => {
            localStorage.setItem("fanBirthDate", '')

          }}
        />
        <Controller
          name="password"
          control={form.control}
          rules={{
            validate: (value) =>
              !confirmPasswordValue ||
              value === confirmPasswordValue ||
              "The passwords do not match",
            required: "Password is required",
            minLength: {
              value: 8,
              message: "Minimum password length 8 characters",
            },
            pattern: {
              value: PASSWORD_REGEX,
              message:
                "Please use uppercase letters, lowercase letters, special characters, and numbers",
            },
          }}
          render={({ field }) => (
            <PasswordField
              inputProps={{ "data-testid": "password" }}
              label="Password"
              placeholder="Enter password"
              error={isDirty && Boolean(errors.password)}
              disabled={isSubmitting}
              helperText={(isDirty && errors.password?.message) || " "}
              {...field}
              ref={null}

            />
          )}
        />
        <Stack width="100%">
          <Controller
            name="confirmPassword"
            control={form.control}
            rules={{
              required: "Confirmed Password is required",
              validate: (value) =>
                value === passwordValue || "The passwords do not match",
            }}
            render={({ field }) => (
              <PasswordField
                inputProps={{ "data-testid": "confirmPassword" }}
                label="Confirm Password"
                placeholder="Enter password"
                error={isDirty && Boolean(errors.confirmPassword)}
                disabled={isSubmitting}
                helperText={(isDirty && errors.confirmPassword?.message) || " "}
                {...field}
                ref={null}

              />
            )}
          />
          <FormControlLabel
            checked={checkedTermsAndConditions}
            control={<Checkbox data-testid="terms" size="small" />}
            label={
              <Typography component={'div'} fontSize={13} textAlign={"left"}>
                <TermsAndCondition />
              </Typography>
            }
            {...form.register("terms", { required: true })}
          />
        </Stack>
      </Stack>
      <Stack margin={2}>
        <SSOIcons />
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={0}
          width="100%"
        >
          <Box marginBottom="24px" display="flex" alignItems="center">
            <Typography>Already have an account?</Typography>
            <Button variant="text" onClick={handleClose}>
              Sign In
            </Button>
          </Box>
        </Stack>
        <Button
          data-testid="nextButton"
          type="button"
          variant="contained"
          onClick={() => setStep(2)}
          disabled={!isFirstStepValid()}
        >
          Continue
        </Button>
      </Stack>
    </>
  );
};

const Step2 = ({ form }: FormStepProps) => {
  const { isDirty, errors, isSubmitting, isValid } = form.formState;
  const [dateValue, setDateValue] = React.useState<unknown>("");
  const [phoneNumber, setPhoneNumber] = useState('');
  const [zipCode, setZipCode] = useState("")
  useEffect(() => {
    if (localStorage.getItem('fanBirthDate') !== "") {
      setDateValue(localStorage.getItem('fanBirthDate'))
    }
  }, [])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangePhoneNumber = (e: any) => {
    const arr = Array.from(e.target.value)
    if (arr[0] !== "0") {
      const formattedPhoneNumber = formatPhoneNumber(e.target.value);
      setPhoneNumber(formattedPhoneNumber);
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeZip = (e: any) => {
    const regex = /^[0-9\b]+$/;
    if (e.target.value === "" || regex.test(e.target.value)) {
      if (e.target.value.length <= 5) {
        setZipCode(e.target.value);
      }
    }
  }
  const currentDate = new Date();
  const DateValidation = currentDate.setFullYear(currentDate.getFullYear() - 18)
  const minDateValidation = currentDate.setFullYear(currentDate.getFullYear() - 100)
  const minformateDate = moment(minDateValidation).format('MM/DD/YYYY')
  const formateDate = moment(DateValidation).format('MM/DD/YYYY')
  return (
    <>
      <Stack direction="column" alignItems="flex-start" spacing={1}>
        <Typography marginBottom={2}>
          Next, let’s collect some details to create your profile.
        </Typography>

        <TextField
          autoFocus
          margin="dense"
          inputProps={{ "data-testid": "firstName" }}
          label="First Name"
          placeholder="Enter First Name"
          InputLabelProps={{
            shrink: true,
          }}
          type="text"
          fullWidth
          error={Boolean(isDirty && errors.firstName)}
          disabled={isSubmitting}
          helperText={(isDirty && errors.firstName?.message) || " "}
          {...form.register("firstName", {
            required: "First Name is required",
          })}
        />
        <TextField
          margin="dense"
          inputProps={{ "data-testid": "lastName" }}
          label="Last Name"
          placeholder="Enter Last Name"
          InputLabelProps={{
            shrink: true,
          }}
          type="text"
          fullWidth
          error={isDirty && Boolean(errors.lastName)}
          disabled={isSubmitting}
          helperText={(isDirty && errors.lastName?.message) || " "}
          {...form.register("lastName", { required: "Last Name is required" })}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            inputFormat={"MM/DD/YYYY"}
            // onError={() => isDirty && Boolean(errors.birthday)}
            disabled={isSubmitting}
            value={dateValue}
            minDate={minformateDate}
            maxDate={formateDate}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={(newValue: any) => {
              localStorage.setItem("fanBirthDate", newValue)
              setDateValue(newValue);
              form.setValue('birthday', format(new Date(newValue), "yyyy-MM-dd"), { shouldValidate: true })
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            renderInput={(params: any) => {
              return <TextField error={isDirty && Boolean(errors.birthday)} label="Birthday" inputProps={{
                placeholder: "MM/DD/YYYY", "data-testid": "birthday",
                readOnly: true, value: params?.inputProps.value
              }} InputProps={params.InputProps}
                inputRef={params.inputRef}
                {...form.register("birthday", {
                  required: "Date of birth is required",
                })}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={(isDirty && errors.birthday?.message) || " "}
              />
            }}

          />
        </LocalizationProvider>
        <TextField
          margin="dense"
          inputProps={{ "data-testid": "zipCode" }}
          label="Zip Code"
          placeholder="Enter Zip Code"
          InputLabelProps={{
            shrink: true,
          }}
          type="text"
          value={zipCode}
          fullWidth
          error={isDirty && Boolean(errors.zipCode)}
          disabled={isSubmitting}
          helperText={(isDirty && errors.zipCode?.message) || " "}
          {...form.register("zipCode", { required: "Zip Code is required" })}
          onChange={(e) => handleChangeZip(e)}
        />
        <TextField
          margin="dense"
          value={phoneNumber}
          inputProps={{ "data-testid": "phoneNumber" }}
          label="Phone Number"
          placeholder="Enter Phone Number"
          InputLabelProps={{
            shrink: true,
          }}
          type="text"
          fullWidth
          error={isDirty && Boolean(errors.phoneNumber)}
          disabled={isSubmitting}
          helperText={(isDirty && errors.phoneNumber?.message) || " "}
          {...form.register("phoneNumber", {
            minLength: {
              value: 12,
              message: "Phone number should be 10 digits"
            },
          })}
          onChange={(e) => handleChangePhoneNumber(e)}
        />
      </Stack>
      <Stack margin={6}>
        <Button
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          data-testid="submitButton"
          type="submit"
          variant="contained"
          disabled={!isValid || isSubmitting}
        >
          Complete
        </Button>
      </Stack>
    </>
  );
};

interface Step3Props {
  watch: UseFormWatch<SignUpFormValues>;
}

const Step3 = ({ watch }: Step3Props) => {
  const notify = useNotifications();
  const navigate = useNavigate();
  const firstName = watch("firstName");
  const password = watch("password");
  const username = watch("email");

  useEffect(() => {
    localStorage.setItem("fanBirthDate", "")

  }, [])
  const login = async () => {
    try {
      await Auth.signIn({
        username,
        password,
      });
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      if (token) {
        window.localStorage.setItem("token", token);
        navigate("/home", { replace: true });
      } else {
        notify.error("Login Failed");
      }
    } catch (error) {
      notify.error("Login Failed");
    }
  };

  return (
    <Stack spacing={2}>
      <Typography
        variant="h4"
        component="h1"
      >{`Welcome ${firstName}!`}</Typography>
      <Typography>You’re all set!</Typography>

      <Box margin="auto">
        <img src={DoneImage} alt="check mark" height={126} width={126}></img>
      </Box>
      <Typography fontSize={16} fontWeight={600}>
        Check your email to verify your account
      </Typography>
      <Box padding={4}>
        <Button variant="contained" fullWidth onClick={login}>
          Let’s go!
        </Button>
      </Box>
    </Stack>
  );
};
